import { IAPIRoutes } from "../../../../config";

/** Sleep inference v1: dev API/CDL until prod is made to work. Possibly prod doesn't have read access to the CDL S3 bucket. */
const SLEEP_QUALITY_INFERENCE_V1_ORIGIN = "https://prod.circadia.link";

export type IPatientRepository = ReturnType<typeof PatientRepositoryFactory>;

export const PatientRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, POST, GET_TEXT, PUT } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const getPatientListAPI: IGetPatientListAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientList.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientEventListAPI: IGetPatientEventListAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientEventList
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.resident_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientSummaryAPI: IGetPatientSummaryAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientSummary
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientCircadiaVitalAPI: IGetPatientCircadiaVitalAPI = async (
    payload
  ) =>
    GET(
      APIRoutes.apiGetPatientCircadiaVital
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientPCCVitalAPI: IGetPatientPCCVitalAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientPCCVital
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientVitalGraphAPI: IGetPatientVitalGraphAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientVitalGraph
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.resident_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientRRGraphAPI: IGetPatientRRGraphAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientRRGraph
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientNotesAPI: IGetPatientNotesAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientNotes
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientNotificationSettingsAPI: IGetPatientNotificationSettingsAPI =
    async (payload) =>
      GET(
        APIRoutes.apiGetPatientNotificationSettings
          .replace("{clinic_id}", payload.clinic_id?.toString())
          .replace("{resident_id}", payload.patient_id?.toString()),
        payload,
        {
          Authorization: `Bearer ${await getAuthenticationToken()}`,
        }
      );

  const getDiagnosesAPI: IGetDiagnosesAPI = async (payload) =>
    GET(
      APIRoutes.apiGetDiagnoses
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{resident_id}", payload.resident_id.toString()),
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPCCNotesAPI: IGetPatientPCCNotesAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientPCCNotes
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientHmacAPI: IGetPatientHmacAPI = async (payload) =>
    GET(APIRoutes.apiGetUserHmac, payload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

  const addPatientNoteAPI: IAddPatientNoteAPI = async (payload) =>
    POST(
      APIRoutes.apiAddPatientNote
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const generateCircadiaIQAPI: IGetGenerateCircadiaIQAPI = async (payload) =>
    POST(
      APIRoutes.apiGenerateCircadiaIQ
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{patient_id}", payload.patient_id?.toString()),
      {
        type: payload.type,
        date: payload.date,
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getCircadiaIQAPI: IGetCircadiaIQAPI = async (payload) =>
    GET(
      APIRoutes.apiGetCircadiaIQ
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{patient_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientListDashboardAPI: IGetPatientListDashboardAPI = async (
    payload
  ) =>
    GET(
      APIRoutes.apiGetPatientListDashboard.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      {
        floor_ids: payload.floorIds,
        no_of_days: payload.no_of_days,
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientProfileAPI: IGetPatientProfileAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientProfile.replace(
        "{patient_id}",
        payload.patient_id.toString()
      ),
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const updatePatientProfileAPI: IUpdatePatientProfileAPI = async (payload) => {
    const { patient_id, ...body } = payload;
    return PUT(
      APIRoutes.apiUpdatePatientProfile.replace(
        "{patient_id}",
        patient_id.toString()
      ),
      body,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const getSleepQualityInferenceV1API: IGetSleepQualityInferenceV1API = async (
    payload
  ) =>
    GET_TEXT(
      `${SLEEP_QUALITY_INFERENCE_V1_ORIGIN}${APIRoutes.apiSleepQualityInfer3sV1
        .replace("{clinic_id}", String(payload.clinic_id))
        .replace("{patient_id}", String(payload.patient_id))}`,
      {
        device_id: payload.device_id,
        date_from: payload.date_from,
        date_to: payload.date_to,
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
        Accept: "text/csv",
      }
    );

  return {
    getPatientListAPI,
    getPatientEventListAPI,
    getPatientSummaryAPI,
    getPatientProfileAPI,
    updatePatientProfileAPI,
    getPatientCircadiaVitalAPI,
    getPatientPCCVitalAPI,
    getPatientVitalGraphAPI,
    getPatientRRGraphAPI,
    getPatientNotesAPI,
    getPatientNotificationSettingsAPI,
    getDiagnosesAPI,
    getPCCNotesAPI,
    addPatientNoteAPI,
    getPatientHmacAPI,
    generateCircadiaIQAPI,
    getCircadiaIQAPI,
    getPatientListDashboardAPI,
    getSleepQualityInferenceV1API,
  };
};
